import React, { useEffect, useMemo, useState } from 'react';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import ActionTypes from '@utils/deviceActions';
import { useTranslation } from 'react-i18next';
import { Form, FormInstance, Radio, Typography } from 'antd';
import useSwr from '@hooks/useSwr';
import { formatCurrency } from '@utils/formatters';
import { LoadingOutlined } from '@ant-design/icons';
import { useActionsLimit } from '@hooks/useActionsLimit';

import * as S from './styles';

const { Text } = Typography;

interface CancellationAndSubstitutionPayloadProps {
  action: ActionTypes.BROKER_CANCELLATION | ActionTypes.BROKER_SUBSTITUTION;
  devices: {
    msisdn: string;
  }[];
}

const useCancellationAndSubstitution = (
  form: FormInstance<any>,
  devicesPayload: { msisdn: string }[],
): DeviceActionProps => {
  const { t } = useTranslation('actions-modal');

  const cancellationType = Form.useWatch('cancellationType', form);
  const isCancellation = cancellationType === 'cancel';

  const [isCalculateFeeLoading, setIsCalculateFeeLoading] = useState(true);

  const onlyMsisdnDevices = useMemo(
    () => devicesPayload?.map(device => device.msisdn),
    [devicesPayload],
  );

  const { data: penaltyData } = useSwr(
    onlyMsisdnDevices?.length > 0 && isCancellation
      ? '/service-proxy/broker/cancellation/calculate-fee'
      : null,
    {
      method: 'post',
      data: onlyMsisdnDevices,
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
    },
  );

  const {
    actionsLimit,
    actionsLimitTitle,
    actionsLimitContent,
    limitHasBeenReached,
  } = useActionsLimit({
    devices: devicesPayload,
    isActive: isCancellation,
    action: ActionTypes.BROKER_CANCELLATION,
  });

  useEffect(() => {
    if (actionsLimit) {
      form.setFieldsValue({ actionsLimit });
    }
  }, [actionsLimit, form]);

  useEffect(() => {
    setIsCalculateFeeLoading(!penaltyData);
  }, [penaltyData]);

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: t('broker-cancellation.confirmation'),
        name: 'cancellationType',
        initialValue: 'substitution',
        showWhenUpload: true,
        content: (
          <S.SubstitutionContainer>
            <Text>
              <p>
                <strong>
                  {t('broker-cancellation.cancellation-substitution-info')}
                </strong>
                &nbsp;
                {t('broker-cancellation.cancellation-substitution-info2')}
              </p>
            </Text>
            <Radio.Group defaultValue="substitution">
              <Radio.Button value="substitution">
                {t('broker-cancellation.substitution')}
                <span
                  style={{
                    marginLeft: 4,
                  }}
                >
                  {t('broker-cancellation.recommended')}
                </span>
              </Radio.Button>
              <Radio.Button value="cancel">
                {t('broker-cancellation.cancel')}
              </Radio.Button>
            </Radio.Group>
          </S.SubstitutionContainer>
        ),
      },
    ];

    if (isCancellation) {
      defaultSteps.push(
        {
          title: actionsLimitTitle,
          name: 'actionsLimit',
          content: actionsLimitContent,
          showWhenUpload: true,
          isNotRequired: true,
        },
        {
          title: t('broker-cancellation.cancellation-fee'),
          name: 'cancellationType',
          initialValue: 'substitution',
          showWhenUpload: true,
          isNotRequired: true,
          content: isCancellation && (
            <S.CancellationAlert
              type="error"
              message={
                isCalculateFeeLoading
                  ? t('broker-cancellation.calculating-fee-for-cancellation')
                  : `${t('broker-cancellation.fee-for-cancellation')}:`
              }
              description={
                isCalculateFeeLoading ? (
                  <LoadingOutlined />
                ) : (
                  <Text strong>{formatCurrency(penaltyData?.fee ?? 0)}</Text>
                )
              }
            />
          ),
        },
      );
    }

    return defaultSteps;
  }, [
    actionsLimitContent,
    actionsLimitTitle,
    isCalculateFeeLoading,
    isCancellation,
    penaltyData?.fee,
    t,
  ]);

  const infoMessage = useMemo(
    () =>
      isCancellation ? (
        <>
          <span
            style={{
              display: 'block',
              fontWeight: 'bold',
              fontSize: '0.85rem',
            }}
          >
            {t('broker-cancellation.cancellation-info-time')}
          </span>
          <span>{t('broker-cancellation.cancellation-info-warning')}</span>
        </>
      ) : (
        <span>{t('broker-cancellation.substitution-info-time')}</span>
      ),
    [isCancellation, t],
  );

  const payloadToSend = useMemo(
    (): CancellationAndSubstitutionPayloadProps => ({
      action: isCancellation
        ? ActionTypes.BROKER_CANCELLATION
        : ActionTypes.BROKER_SUBSTITUTION,
      devices: devicesPayload,
    }),
    [devicesPayload, isCancellation],
  );

  return {
    steps,
    payloadToSend,
    infoMessage,
    confirmDisabled:
      isCancellation && (isCalculateFeeLoading || limitHasBeenReached),
  };
};

export default useCancellationAndSubstitution;
